import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { useApproveDocumentMutation } from 'hooks/useApproveDocumentMutation';

interface ApproveDocumentMenuProps {
  contentId: string;
}

export function ApproveDocumentMenu({ contentId }: ApproveDocumentMenuProps) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notes, setNotes] = useState('');

  const { mutate: approveDocument, isPending } = useApproveDocumentMutation();

  const handleToggle = (event?: React.MouseEvent<HTMLElement>) => {
    if (anchorEl || !event) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleSubmit = () => {
    approveDocument(
      { contentId, notes: notes.trim() || undefined },
      {
        onSuccess: () => {
          setNotes('');
          setAnchorEl(null);
        },
      }
    );
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        className="approval-action-button"
        onClick={handleToggle}
        endIcon={anchorEl ? <ExpandLess /> : <ExpandMore />}
      >
        {t('share_document_v3.approve_request_button')}
      </Button>
      <Popover
        disableScrollLock
        data-testid="approve-document-menu"
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => handleToggle()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ '& .MuiPaper-root': { borderRadius: 4 } }}
      >
        <Card sx={{ minWidth: 344, maxWidth: 344 }}>
          <CardContent>
            <Stack gap={2}>
              <Stack>
                <Typography variant="h5" fontWeight={600}>
                  {t('share_document_v3.approve_document_menu.title')}
                </Typography>
                <Typography variant="body2" mt={0} color="text.secondary">
                  {t('share_document_v3.approve_document_menu.copy')}
                </Typography>
              </Stack>
              <TextField
                label={t('share_document_v3.approve_document_menu.notes_label')}
                placeholder={t('share_document_v3.approve_document_menu.notes_placeholder')}
                multiline
                rows={3}
                fullWidth
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
              <Button variant="contained" color="primary" onClick={handleSubmit} disabled={isPending}>
                {t('share_document_v3.approve_document_menu.submit_button')}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Popover>
    </>
  );
}
